import { useEffect, useState } from "react";
import { zipStore, safeFileName } from "@greg-chick/shared";
import { loadManifest, type ManifestEntry } from "../lib/contentApi";
import { assetFileName, bytesBlob, fetchBytes, saveBlob, csvBytes } from "../lib/download";
import { groupAssets } from "../lib/assetGroups";
import { ActionButton } from "./ActionButton";

/**
 * 📦 **올린 그림 전부 내려받기** — 지금 웹이 쓰는 그림을 통째로 내 컴퓨터에.
 *
 * ## 왜 필요한가
 * 관리자로 올린 그림은 R2 에만 있다. 인쇄에 쓰려고·다른 도구로 다듬으려고 원본을 찾으면,
 * 몇 달 전 어느 폴더에 뒀는지부터 찾아야 한다. **올린 것을 다시 꺼낼 길**이 있어야 한다.
 * (자리 하나씩은 각 칸의 [⬇ 내려받기]로 받는다. 여기는 한꺼번에.)
 *
 * ## 🔴 실패를 숨기지 않는다
 * 200장 중 3장이 조용히 빠지면 운영자는 「다 받았다」고 믿는다. 몇 개가 왜 실패했는지
 * 이름까지 말하고, **받은 것은 그래도 준다**(전부 아니면 아무것도, 는 더 나쁘다).
 *
 * ## 폴더로 나눠 담는다
 * 200장을 한 폴더에 쏟으면 받아도 못 쓴다. 메뉴 이름으로 나눈다(`assetGroups`).
 * 파일 이름은 **자리 이름 그대로** — 나중에 어느 칸이었는지 알아야 다시 올린다.
 * 사람이 읽을 이름은 같이 담기는 `목록.csv` 에 적는다.
 */
export function AssetBackupPanel() {
  const [assets, setAssets] = useState<Record<string, ManifestEntry> | null>(null);
  const [done, setDone] = useState(0);
  const [failed, setFailed] = useState<string[]>([]);

  useEffect(() => {
    void loadManifest()
      .then((m) => setAssets(m.assets))
      .catch(() => setAssets({}));
  }, []);

  const groups = groupAssets(assets ?? {});
  const total = groups.reduce((s, g) => s + g.items.length, 0);

  async function downloadAll() {
    setDone(0);
    setFailed([]);
    const entries: { name: string; bytes: Uint8Array }[] = [];
    const rows = [["폴더", "파일", "자리 이름", "설명"]];
    const bad: string[] = [];
    for (const g of groups) {
      for (const it of g.items) {
        try {
          const bytes = await fetchBytes(it.url);
          const name = assetFileName(it.key, it.url);
          entries.push({ name: `${safeFileName(g.folder)}/${name}`, bytes });
          rows.push([g.folder, name, it.key, assets?.[it.key]?.alt ?? ""]);
        } catch {
          // 한 장이 실패해도 나머지는 받는다 — 대신 **이름을 남긴다.**
          bad.push(it.key);
        }
        setDone((n) => n + 1);
        setFailed([...bad]);
      }
    }
    if (entries.length === 0) throw new Error("받을 그림이 없어요.");
    if (bad.length > 0) {
      rows.push([], ["⚠️ 못 받은 자리", ...bad]);
    }
    entries.push({
      name: "목록.csv",
      bytes: csvBytes(rows.map((r) => r.map((c) => `"${(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n")),
    });
    const zip = zipStore(entries);
    saveBlob(bytesBlob(zip, "application/zip"), "그림전체.zip");
    if (bad.length > 0) {
      throw new Error(
        `${entries.length - 1}장을 받았어요. ${bad.length}장은 못 받았어요 — 묶음 안 목록.csv 아래쪽에 적어 뒀어요.`,
      );
    }
  }

  async function downloadGroup(folder: string) {
    const g = groups.find((x) => x.folder === folder);
    if (!g) throw new Error("받을 그림이 없어요.");
    const entries: { name: string; bytes: Uint8Array }[] = [];
    const bad: string[] = [];
    for (const it of g.items) {
      try {
        entries.push({ name: assetFileName(it.key, it.url), bytes: await fetchBytes(it.url) });
      } catch {
        bad.push(it.key);
      }
    }
    if (entries.length === 0) throw new Error("한 장도 받지 못했어요.");
    saveBlob(bytesBlob(zipStore(entries), "application/zip"), `${safeFileName(folder)}.zip`);
    if (bad.length > 0) throw new Error(`${entries.length}장을 받았어요. 못 받은 것: ${bad.join(", ")}`);
  }

  return (
    <section className="as-sec">
      <h2 className="as-sec__h">📦 올린 그림 내려받기</h2>
      <p className="hint">
        지금 웹이 쓰는 그림·소리를 내 컴퓨터로 받아요. 인쇄에 쓰거나 다른 도구로 다듬을 때,
        <b> 원본 파일을 어디 뒀는지 찾지 않아도 돼요.</b> 파일 이름은 <b>자리 이름 그대로</b>라
        나중에 그 칸에 다시 올리기 쉬워요.
      </p>

      {assets === null ? (
        <p className="hint">불러오는 중…</p>
      ) : total === 0 ? (
        <p className="hint">아직 올린 그림이 없어요.</p>
      ) : (
        <>
          <ul className="abk__list">
            {groups.map((g) => (
              <li key={g.folder}>
                <b>{g.folder}</b> <span>{g.items.length}개</span>
                <ActionButton
                  className="btn btn--sm"
                  onAction={() => downloadGroup(g.folder)}
                  doneLabel="저장했어요"
                >
                  ⬇ 이것만
                </ActionButton>
              </li>
            ))}
          </ul>
          <p className="hint">
            <ActionButton
              className="btn btn--primary btn--sm"
              onAction={downloadAll}
              doneLabel="저장했어요 — 받은 폴더를 확인해 주세요"
            >
              ⬇ 전부 내려받기 ({total}개)
            </ActionButton>{" "}
            {done > 0 && done < total ? `${done} / ${total} 받는 중…` : null}
            {failed.length > 0 ? ` · 못 받은 것 ${failed.length}개` : null}
          </p>
          <p className="hint">
            묶음 안에 <b>목록.csv</b> 가 같이 들어 있어요 — 어느 파일이 어느 칸의 무엇인지 적혀
            있어요. 장수가 많으면 조금 걸려요(한 장씩 받아 옵니다).
          </p>
        </>
      )}
    </section>
  );
}
